import './Layout.scss';

import { ButtonProps, Loader, MenuItemProps, TopNavigation, TopNavigationProps } from '@trackman/web-shared-components';
import { Can, Cannot } from 'components/Can';
import { ExpiredInvite, FailedInvite } from 'pages/Forbidden/Error';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { AppPermissions } from 'utilities/Constants';
import AppSwitcher from '../AppSwitcher/AppSwitcher';
import { ApplicationUserRights } from 'stores/ApplicationUserRightsStore';
import { DefaultRoutes } from 'Routes';
import LoggedOutInfoModal from '../LoggedOutInfoModal/LoggedOutInfoModal';
import { NoPermissions } from 'pages/NoPermissions';
import { Notifications } from 'components/Notifications';
import ProfileIconMenu from '../ProfileIconMenu/ProfileIconMenu';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';

type Props = {
  children: React.ReactNode;
};

type TopLevelMenuLabel = 'Organizations' | 'Issue Reports' | 'Tracking Diagnostics' | 'Diagnostics Packages';

type Navigation = Array<
  {
    label: TopLevelMenuLabel;
  } & (
    | ({
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
      href: string;
      need: string[];
      submenu?: undefined;
    } & ButtonProps)
    | {
      onClick?: undefined;
      href?: undefined;
      need?: undefined;
      submenu: SubmenuItem[];
    }
  )
>;

type SubmenuItem = {
  label: string;
  onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  href: string;
  need: string[];
} & MenuItemProps;

type ValueOf<T> = T[keyof T];
type DefaultRoute = ValueOf<typeof DefaultRoutes>;

const routeToActiveLabel: Record<DefaultRoute, TopLevelMenuLabel> = {
  '/': 'Organizations',
  '/users': 'Organizations',
  '/issue-reports': 'Issue Reports',
  '/tracking-diagnostics': 'Tracking Diagnostics',
  '/diagnostics-packages': 'Diagnostics Packages',
};

const Layout = observer(({ children }: Props) => {
  const { t } = useTranslation();
  const ss = useStores().startupStore;
  const history = useHistory();
  const location = useLocation();

  const [displayContent, setDisplayContent] = useState(false);

  useEffect(() => {
    if (ss.hasBearerToken) setDisplayContent(true);
  }, [ss.hasBearerToken]);

  const menuSrtucture: Navigation = useMemo(
    () => [
      {
        label: t('organizations'),
        onClick: (e) => {
          e.preventDefault();
          history.push(DefaultRoutes.OrganizationsPage);
        },
        href: DefaultRoutes.OrganizationsPage,
        need: [],
      },
      {
        label: t('UserDiagnostics.Title'),
        onClick: (e) => {
          e.preventDefault();
          history.push(DefaultRoutes.UserDiagnosticsPage);
        },
        href: DefaultRoutes.UserDiagnosticsPage,
        need: [],
      },
      {
        label: t('TrackingDiagnostics.Title'),
        onClick: (e) => {
          e.preventDefault();
          history.push(DefaultRoutes.TrackingDiagnosticsPage);
        },
        href: DefaultRoutes.TrackingDiagnosticsPage,
        need: [],
      },
      {
        label: t('DiagnosticsPackages.Title'),
        onClick: (e) => {
          e.preventDefault();
          history.push(DefaultRoutes.DiagnosticsPackagesPage);
        },
        href: DefaultRoutes.DiagnosticsPackagesPage,
        need: [],
      }
    ],
    [history]
  );

  const activeNavigationLabel = useMemo(() => {
    const route = '/' + (location.pathname.split('/').filter((x) => x !== '')[0] ?? '');
    return routeToActiveLabel[route];
  }, [location.pathname]);

  const navigation: TopNavigationProps['navigation'] = useMemo(() => {
    if (!ss.hasBearerToken) return [];

    const menuSrtuctureTemp: Navigation = [];

    menuSrtucture.forEach((item) => {
      if (item.need && ApplicationUserRights.IsAllowed(item.need)) menuSrtuctureTemp.push(item);
      if (item.submenu) {
        const submenuToMutate: SubmenuItem[] = [];
        menuSrtuctureTemp.push({ label: item.label, submenu: submenuToMutate });
        item.submenu.forEach((subItem) => {
          if (ApplicationUserRights.IsAllowed(subItem.need)) submenuToMutate.push(subItem);
        });
        if (submenuToMutate.length === 0) menuSrtuctureTemp.pop();
      }
    });
    return menuSrtuctureTemp;
  }, [ss.hasBearerToken, menuSrtucture]);

  return (
    <div className='layout-content-container'>
      <div className='top-navigation'>
        <TopNavigation
          appName={'Football Admin Portal'}
          navigation={navigation}
          activeNavigationLabel={t(activeNavigationLabel)}
          utilities={
            ss.hasBearerToken && !ss.isInitializing
              ? [
                { key: 'profile-icon-menu', component: <ProfileIconMenu setDisplayContent={setDisplayContent} /> },
                {
                  key: 'app-switcher-icon-menu',
                  component: <AppSwitcher setDisplayContent={setDisplayContent} />,
                },
              ]
              : []
          }
          animated
          displayContent={displayContent}
          onLogoClick={() => history.push(DefaultRoutes.OrganizationsPage)}
        />
      </div>
      {!displayContent && (
        <div className='spinner'>
          <Loader size='large' />
        </div>
      )}
      {ss.hasBearerToken && (
        <div className={`content ${displayContent ? '' : 'content-hidden'}`}>
          <Notifications />
          <Can need={AppPermissions.PortalAccess}>
            <div style={{ paddingTop: '26px' }}>
              <div className='container-fluid'>{children}</div>
            </div>
          </Can>
          <Cannot need={AppPermissions.PortalAccess}>
            <div className='forbidden-page'>
              <Switch>
                <Route path='/invite_expired' component={ExpiredInvite} />
                <Route path='/invite_failed' component={FailedInvite} />
                <Route render={() => <NoPermissions missingPermission={AppPermissions.PortalAccess.join(', ')} />} />
              </Switch>
            </div>
          </Cannot>
        </div>
      )}
      {ss.config && <LoggedOutInfoModal logoutUrl={ss.config.logoutUrl} />}
    </div>
  );
});

export default Layout;
